// src/pages/customer/customerpanel/OrdersPanel.jsx
import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BASE_URL || 'http://localhost:4000'}${url}`;
};

const STATUS = {
  pending:          { label: 'Pending',          color: 'bg-amber-100 text-amber-700',     icon: 'hourglass_empty' },
  pending_payment:  { label: 'Awaiting Payment', color: 'bg-orange-100 text-orange-700',   icon: 'payment'         },
  confirmed:        { label: 'Confirmed',        color: 'bg-blue-100 text-blue-700',       icon: 'check_circle'    },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-700',   icon: 'local_shipping'  },
  completed:        { label: 'Delivered',        color: 'bg-emerald-100 text-emerald-700', icon: 'task_alt'        },
  cancelled:        { label: 'Cancelled',        color: 'bg-red-100 text-red-600',         icon: 'cancel'          },
};

const STEPS = [
  { key: 'pending',          label: 'Order Placed'     },
  { key: 'confirmed',        label: 'Farmer Accepted'  },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'completed',        label: 'Delivered'        },
];

const stepIndex = (status) =>
  ({ pending: 0, pending_payment: 0, confirmed: 1, out_for_delivery: 2, completed: 3 }[status] ?? 0);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// ── Single order card ──────────────────────────────────────
function OrderCard({ order, onPayNow, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS[order.status] || STATUS.pending;
  const currentStep = stepIndex(order.status);
  const isCancelled = order.status === 'cancelled';
  const [showCancel, setShowCancel] = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputing, setDisputing] = useState(false);
  const [editForm,   setEditForm]   = useState({
    delivery_address: order.delivery_address || '',
    note: order.note || '',
  });

  return (
    <>
    {showCancel && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm mx-4 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-red-600 text-[24px]">cancel</span>
          </div>
          <h3 className="font-black text-on-surface text-lg">Cancel Order?</h3>
          <p className="text-sm text-secondary">
            Cancel order #{order.id} for <strong>{order.animal_name}</strong>?
            The animal will be relisted for other buyers.
          </p>
          <div className="flex gap-3">
            <button onClick={() => setShowCancel(false)}
              className="flex-1 py-2.5 rounded-xl border border-outline-variant/30
                        text-sm font-semibold text-secondary hover:bg-surface-container transition-all">
              Keep Order
            </button>
            <button disabled={cancelling}
              onClick={async () => {
                setCancelling(true);
                try {
                  await api.patch(
                    order._type === 'meat'
                      ? `/api/customer/shop-orders/${order.id}/cancel`
                      : `/api/customer/orders/${order.id}/cancel`
                  );
                  setShowCancel(false);
                  if (onRefresh) onRefresh();
                } catch (e) {
                  alert(e.response?.data?.error || 'Failed to cancel');
                } finally { setCancelling(false); }
              }}
              className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold
                        hover:bg-red-700 transition-all disabled:opacity-50">
              {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showEdit && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm mx-4 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-blue-600 text-[24px]">edit_location</span>
          </div>
          <h3 className="font-black text-on-surface text-lg">Edit Delivery Details</h3>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
                Delivery Address
              </label>
              <textarea rows={3} value={editForm.delivery_address}
                onChange={e => setEditForm(f => ({ ...f, delivery_address: e.target.value }))}
                className="w-full border border-outline-variant/30 rounded-xl p-3 text-sm
                          outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Enter full delivery address..." />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
                Note / Preferred Time
              </label>
              <textarea rows={2} value={editForm.note}
                onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                className="w-full border border-outline-variant/30 rounded-xl p-3 text-sm
                          outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="e.g. Please deliver after 5 PM..." />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowEdit(false)}
              className="flex-1 py-2.5 rounded-xl border border-outline-variant/30
                        text-sm font-semibold text-secondary hover:bg-surface-container transition-all">
              Cancel
            </button>
            <button disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await api.patch(
                    order._type === 'meat'
                      ? `/api/customer/shop-orders/${order.id}/delivery`
                      : `/api/customer/orders/${order.id}/delivery`,
                    editForm
                  );
                  setShowEdit(false);
                  if (onRefresh) onRefresh();
                } catch (e) {
                  alert(e.response?.data?.error || 'Failed to update');
                } finally { setSaving(false); }
              }}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold
                        hover:bg-blue-700 transition-all disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showDispute && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm mx-4 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-red-600 text-[24px]">report</span>
          </div>
          <h3 className="font-black text-on-surface text-lg">Raise a Dispute</h3>
          <p className="text-xs text-secondary">Order #{order.id} — {order.animal_name}</p>
          <textarea rows={3} value={disputeReason}
            onChange={e => setDisputeReason(e.target.value)}
            placeholder="Describe the issue with this payment..."
            className="w-full border border-outline-variant/30 rounded-xl p-3 text-sm
                      outline-none focus:ring-2 focus:ring-red-400 resize-none" />
          <div className="flex gap-3">
            <button onClick={() => { setShowDispute(false); setDisputeReason(''); }}
              className="flex-1 py-2.5 rounded-xl border border-outline-variant/30
                        text-sm font-semibold text-secondary hover:bg-surface-container transition-all">
              Cancel
            </button>
            <button disabled={disputing || !disputeReason.trim()}
              onClick={async () => {
                setDisputing(true);
                try {
                  await api.post('/api/payment/dispute', {
                    order_id: order.id,
                    order_type: order._type === 'meat' ? 'shop' : 'animal',
                    reason: disputeReason,
                  });
                  setShowDispute(false);
                  setDisputeReason('');
                  alert('Dispute submitted. Admin will review shortly.');
                } catch (e) {
                  alert(e.response?.data?.error || 'Failed to raise dispute');
                } finally { setDisputing(false); }
              }}
              className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold
                        hover:bg-red-700 transition-all disabled:opacity-50">
              {disputing ? 'Submitting…' : 'Submit Dispute'}
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                    overflow-hidden hover:border-primary/20 transition-all">

      {/* Top row */}
      <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 cursor-pointer overflow-hidden"
            onClick={() => setExpanded(!expanded)}>
        {order.image_url
          ? <img src={getImageUrl(order.image_url)} alt={order.animal_name}
                 className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0" />
          : <div className="w-16 h-16 rounded-xl bg-surface-container
                            flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-2xl text-outline-variant">pets</span>
            </div>
        }

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-on-surface">{order.animal_name || '—'}</p>
            {order.category && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full
                               bg-primary/10 text-primary">
                {order.category}
              </span>
            )}
          </div>
          <p className="text-[11px] text-secondary font-mono mt-0.5">
            Order #{order.id} · {new Date(order.created_at).toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric'
            })} · {new Date(order.created_at).toLocaleTimeString('en-IN', {
              hour: '2-digit', minute: '2-digit'
            })}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="material-symbols-outlined text-[13px] text-secondary">
              {order.payment_method === 'online' ? 'credit_card' : 'payments'}
            </span>
            <span className="text-[11px] text-secondary">
              {order.payment_method === 'online' ? 'Online Payment' : 'Cash on Delivery'}
            </span>
            {order.payment_status === 'paid' && (
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50
                               px-1.5 py-0.5 rounded-full">Paid ✓</span>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0 min-w-0">
          <p className="font-black text-primary text-sm sm:text-lg whitespace-nowrap">
            ₹{Number(order.amount || 0).toLocaleString('en-IN')}
          </p>
          <span className={`text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap block ${st.color}`}>
            {st.label}
          </span>
        </div>

        <span className="p-1 text-secondary flex-shrink-0">
          <span className="material-symbols-outlined text-[20px]">
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
        </span>
      </div>

      {/* Pay Now banner */}
      {order.status === 'pending_payment' && (
        <div className="mx-4 mb-4 px-4 py-3 bg-orange-50 border border-orange-200
                        rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-orange-500 text-[20px]">payment</span>
            <div>
              <p className="text-sm font-bold text-orange-800">Payment Pending</p>
              <p className="text-[11px] text-orange-600">Complete payment to confirm your order</p>
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onPayNow(order); }}
              className="px-4 py-2 rounded-xl bg-orange-500 text-white font-bold text-sm
                        hover:bg-orange-600 transition-all flex-shrink-0">
              Pay Now
          </button>
        </div>
      )}

      {/* OTP banner */}
      {order.status === 'out_for_delivery' && order.delivery_otp && (
        <div className="mx-4 mb-4 px-4 py-4 bg-purple-50 border border-purple-200
                        rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center
                            justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-purple-600 text-[22px]">pin</span>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-purple-600">
                Your Delivery OTP
              </p>
              <p className="text-3xl font-black text-purple-800 tracking-[0.3em] leading-tight">
                {order.delivery_otp}
              </p>
              <p className="text-[11px] text-purple-600 mt-0.5">
                Tell this OTP to the delivery person when they arrive
              </p>
            </div>
          </div>
          <span className="material-symbols-outlined text-purple-200 text-[48px]">
            local_shipping
          </span>
        </div>
      )}

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-outline-variant/10 p-3 sm:p-4 space-y-3 sm:space-y-4
                        bg-surface-container-low/40">

          {/* Order & Seller Info */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
            <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">receipt_long</span>
              Order Info
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">
                  {order._type === 'meat' ? 'Product' : 'Animal'}
                </p>
                <p className="text-sm font-bold text-on-surface">
                  {order.animal_name || order.product_name || '—'}
                </p>
                {order.category && <p className="text-[11px] text-secondary">{order.category}</p>}
                {order.breed && <p className="text-[11px] text-secondary">{order.breed}</p>}
              </div>

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Seller</p>
                <p className="text-sm font-bold text-on-surface">{order.seller_name || '—'}</p>
                {order.seller_phone && (
                  <a href={`tel:${order.seller_phone}`}
                    className="text-[11px] text-primary hover:underline flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">call</span>
                    {order.seller_phone}
                  </a>
                )}
              </div>

              {Number(order.order_quantity || order.quantity) > 1 && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Qty</p>
                  <p className="text-sm font-bold text-on-surface">× {order.order_quantity || order.quantity}</p>
                </div>
              )}

              {order.weight_kg && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Weight</p>
                  <p className="text-sm font-bold text-on-surface">
                    {order.weight_kg} {order.unit || 'kg'}
                  </p>
                </div>
              )}

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Placed On</p>
                <p className="text-sm font-bold text-on-surface">
                  {new Date(order.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  })}
                </p>
                <p className="text-[11px] text-secondary">
                  {new Date(order.created_at).toLocaleTimeString('en-IN', {
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>

              {order.updated_at && order.updated_at !== order.created_at && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Last Updated</p>
                  <p className="text-sm font-bold text-on-surface">
                    {new Date(order.updated_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </p>
                  <p className="text-[11px] text-secondary">
                    {new Date(order.updated_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

            </div>
          </div>

          {/* Journey tracker */}
          {!isCancelled && (
            <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
              <p className="text-[10px] font-black uppercase tracking-widest
                            text-secondary mb-4 sm:mb-5">Order Journey</p>
              <div className="flex items-start justify-between relative">
                <div className="absolute left-3 right-3 sm:left-4 sm:right-4 top-4 h-0.5 bg-surface-container" />
                <div className="absolute left-3 sm:left-4 top-4 h-0.5 bg-primary transition-all duration-700"
                     style={{ width: `calc(${(currentStep / 3) * 100}% - 1.5rem)` }} />
                {STEPS.map((step, i) => (
                  <div key={step.key} className="flex flex-col items-center gap-1.5 sm:gap-2 z-10 flex-1">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center
                                     border-2 transition-all ${
                      i < currentStep
                        ? 'bg-primary border-primary text-white'
                        : i === currentStep
                          ? 'bg-primary border-primary text-white ring-4 ring-primary/20'
                          : 'bg-white border-outline-variant/30 text-outline-variant'
                    }`}>
                      {i < currentStep
                        ? <span className="material-symbols-outlined text-[14px] sm:text-[16px]">check</span>
                        : <span className="text-[10px] sm:text-xs font-black">{i + 1}</span>
                      }
                    </div>
                    <p className={`text-[9px] sm:text-[10px] font-bold text-center max-w-[56px] sm:max-w-[64px] leading-tight ${
                      i <= currentStep ? 'text-primary' : 'text-outline-variant'
                    }`}>{step.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:gap-4">

            {/* Delivery address */}
            <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
              <p className="text-[10px] font-black uppercase tracking-widest
                            text-secondary mb-2 sm:mb-3 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">local_shipping</span>
                Delivery Details
              </p>
              <div className="space-y-2 text-xs sm:text-sm text-secondary">
                {order.delivery_address
                  ? <div className="flex gap-2">
                      <span className="material-symbols-outlined text-[16px] mt-0.5 flex-shrink-0">
                        home
                      </span>
                      <span className="break-words">{order.delivery_address}</span>
                    </div>
                  : <p className="text-outline italic text-xs">No delivery address provided</p>
                }
                {order.note && (
                  <div className="pt-2 border-t border-outline-variant/10">
                    <p className="text-[10px] font-bold uppercase tracking-widest
                                  text-secondary mb-1">Your Note</p>
                    <p className="text-xs italic break-words">"{order.note}"</p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery boy */}
            {order.delivery_boy_name ? (
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
                <p className="text-[10px] font-black uppercase tracking-widest
                              text-secondary mb-2 sm:mb-3 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">delivery_dining</span>
                  Delivery Person
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center
                                  justify-center text-purple-700 font-black flex-shrink-0">
                    {order.delivery_boy_name[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate">{order.delivery_boy_name}</p>
                    <a href={`tel:${order.delivery_boy_phone}`}
                       className="text-[11px] text-primary hover:underline flex items-center gap-1">
                      <span className="material-symbols-outlined text-[13px]">call</span>
                      <span className="truncate">{order.delivery_boy_phone}</span>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10
                              flex items-center gap-3">
                <span className="material-symbols-outlined text-outline-variant text-[24px] sm:text-[28px] flex-shrink-0">
                  delivery_dining
                </span>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-secondary">Delivery not assigned yet</p>
                  <p className="text-[10px] sm:text-[11px] text-outline">
                    Farmer will assign a delivery person soon
                  </p>
                </div>
              </div>
            )}

            {/* Payment summary */}
            <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
              <p className="text-[10px] font-black uppercase tracking-widest
                            text-secondary mb-2 sm:mb-3 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">receipt</span>
                Payment Summary
              </p>
              <div className="flex justify-between items-start sm:items-center gap-3">
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-secondary">
                    <span className="material-symbols-outlined text-[16px] flex-shrink-0">
                      {order.payment_method === 'online' ? 'credit_card' : 'payments'}
                    </span>
                    <span className="break-words">
                      {order.payment_method === 'online'
                        ? 'Paid online via Razorpay'
                        : 'Cash on Delivery'}
                    </span>
                  </div>
                  {order.razorpay_payment_id && (
                    <p className="text-[10px] font-mono text-outline break-all">
                      Txn: {order.razorpay_payment_id}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl sm:text-2xl font-black text-primary">
                    ₹{Number(order.amount || 0).toLocaleString('en-IN')}
                  </p>
                  <span className={`text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    order.payment_status === 'paid'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {order.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}
                  </span>
                </div>
              </div>
            </div>
            {['pending', 'confirmed'].includes(order.status) && (
              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowEdit(true)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                             border border-blue-200 text-blue-600 text-xs font-bold
                             hover:bg-blue-50 transition-all">
                  <span className="material-symbols-outlined text-[16px]">edit_location</span>
                  Edit Delivery
                </button>
                <button onClick={() => setShowCancel(true)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                             border border-red-200 text-red-600 text-xs font-bold
                             hover:bg-red-50 transition-all">
                  <span className="material-symbols-outlined text-[16px]">cancel</span>
                  Cancel Order
                </button>
              </div>
            )}
            {order.status === 'completed' && order.payment_method === 'online' && order.payment_status === 'paid' && (
              <button onClick={() => setShowDispute(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                          border border-red-200 text-red-600 text-xs font-bold
                          hover:bg-red-50 transition-all mt-2">
                <span className="material-symbols-outlined text-[16px]">report</span>
                Raise Payment Dispute
              </button>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ── Main export ───────────────────────────────────────────
export default function OrdersPanel({ orders = [], loading, onRefresh, user }) {
  const [shopOrders, setShopOrders] = useState([]);
  const [filter, setFilter]         = useState('all'); 

    useEffect(() => {
    api.get('/api/customer/shop-orders')
    .then(r => setShopOrders(r.data)).catch(console.error);
  }, []);

  const allOrders = [
     ...orders.map(o => ({ ...o, _type: 'livestock' })),
    ...shopOrders.map(o => ({
      ...o,
      animal_name: o.product_name,
      _type: 'meat',
    }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handlePayNow = async (order) => {
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Razorpay failed to load. Check your connection.'); return; }

      const { data } = await api.post(
        order._type === 'meat' 
        ? '/api/payment/create-shop-order' 
        : '/api/payment/create-order',
        { order_id: order.id }
      );

      const options = {
        key:         data.key_id,
        amount:      data.amount,
        currency:    data.currency,
        name:        'Elite Goats',
        description: `Order #${order.id} — ${order.animal_name}`,
        order_id:    data.razorpay_order_id,
        prefill:     { name: user?.name || '', contact: user?.phone || '', email: user?.email || '' },
        theme:       { color: '#1a6b3c' },
        handler: async (response) => {
          await api.post(
            order._type === 'meat' 
            ? '/api/payment/verify-shop' 
            : '/api/payment/verify',
            {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              order_id:            order.id,
            }
          );
          if (onRefresh) onRefresh();
        },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      alert(err.response?.data?.error || 'Payment initiation failed');
    }
  };

  const counts = {
    total:    allOrders.length,
    active:   allOrders.filter(o => ['pending','confirmed'].includes(o.status)).length,
    onWay:    allOrders.filter(o => o.status === 'out_for_delivery').length,
    done:     allOrders.filter(o => o.status === 'completed').length,
  };

  const filteredOrders = filter === 'all'
    ? allOrders
    : filter === 'active'
      ? allOrders.filter(o => ['pending', 'confirmed'].includes(o.status))
      : allOrders.filter(o => o.status === filter);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">My Orders</h2>
        <p className="text-sm text-secondary mt-1">Track all your purchases and deliveries.</p>
      </div>

      {allOrders.length > 0 && (
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
          {[
            { label: 'Total Orders', val: counts.total,  key: 'all',              color: 'bg-white border border-outline-variant/20 text-secondary', activeColor: 'bg-primary text-white border-primary' },
            { label: 'Active',       val: counts.active, key: 'active',           color: 'bg-amber-50 text-amber-700',     activeColor: 'bg-amber-500 text-white'   },
            { label: 'On the Way',   val: counts.onWay,  key: 'out_for_delivery', color: 'bg-purple-50 text-purple-700',   activeColor: 'bg-purple-500 text-white'  },
            { label: 'Delivered',    val: counts.done,   key: 'completed',        color: 'bg-emerald-50 text-emerald-700', activeColor: 'bg-emerald-500 text-white' },
          ].map(p => (
            <button key={p.label}
              onClick={() => setFilter(p.key)}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition-all
                          ${filter === p.key ? p.activeColor : p.color}`}>
              {p.val} {p.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm
                        border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">receipt_long</span>
          <p className="mt-3 font-bold text-on-surface">No orders yet</p>
          <p className="text-xs text-secondary mt-1">
            Your order history will appear here once you make a purchase.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map(order => (
            <OrderCard key={`${order._type ?? 'livestock'}-${order.id}`} order={order} onPayNow={handlePayNow} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}